/**
 * Pure helpers for Operate Mode (Running State) presentation.
 *
 * The variance MATH lives in `@thekwallace/backtime-shared`
 * (`getRunningElementTimings` / `summarizeVariance`) and is deliberately NOT
 * duplicated here. This module only covers the two things the webapp needs on
 * top of it:
 *
 *  1. Moving between the app's "absolute seconds since the BROWSER's local
 *     midnight" clock and the absolute ISO-8601 instants the API speaks — in
 *     both directions, and for a whole `RunningState` at once.
 *  2. Formatting a variance for display so it is never conveyed by colour
 *     alone — every readout carries a sign AND a word ("behind"/"ahead").
 *
 * See backtime-docs/plans/running-state-implementation.md §6.1 and §9.1.
 */

import {
  localizeRunningState,
  type LocalizedRunningState,
  type RunningElement,
  type RunningState,
} from '@thekwallace/backtime-shared';

/** Which side of the plan a variance falls on. */
export type VarianceTone = 'behind' | 'ahead' | 'on-time';

/**
 * Variance smaller than this reads as "on time" rather than flapping between
 * +0:01 behind and −0:01 ahead every tick.
 */
export const ON_TIME_TOLERANCE_SECONDS = 5;

/**
 * Convert a time expressed in the app's absolute-seconds frame (seconds since
 * the client's local midnight, which may extend past 86400 for tomorrow) into
 * an absolute ISO-8601 instant.
 *
 * Anchored off a known (nowMs, nowAbsoluteSeconds) pair from the synced clock
 * so both values share one frame — this is what makes the result correct
 * regardless of the client's time zone.
 *
 * @param absoluteSeconds - Target time in absolute seconds
 * @param nowAbsoluteSeconds - "Now" in the same absolute-seconds frame
 * @param nowMs - "Now" as a Unix epoch millisecond timestamp
 * @returns ISO-8601 instant string (UTC)
 */
export function absoluteSecondsToIsoInstant(
  absoluteSeconds: number,
  nowAbsoluteSeconds: number,
  nowMs: number
): string {
  const deltaMs = (absoluteSeconds - nowAbsoluteSeconds) * 1000;
  return new Date(nowMs + deltaMs).toISOString();
}

/**
 * Convert an absolute ISO-8601 instant back into the app's absolute-seconds frame.
 *
 * The exact inverse of `absoluteSecondsToIsoInstant`, and the reason running-state
 * rendering can pin itself to the occurrence the run was actually STARTED against
 * rather than whichever occurrence the app currently has selected.
 *
 * ## Two different `occurrenceStartSeconds` — use the localized one
 *
 * Since 0.9.0 that name refers to two different numbers, and they are only equal
 * when the browser and the timeline share a zone:
 *
 *  - **`RunningStateResponse.occurrenceStartSeconds`** (the REST envelope, §6.2)
 *    is seconds-from-midnight in the **TIMELINE's** timezone. This app never
 *    renders in that frame, so consuming it raw skews every planned time on
 *    screen by the zone offset — the defect §6.1 was rewritten to kill. It is
 *    also **absent from the WebSocket broadcast**, which carries only a bare
 *    `RunningState`, so a client pinned to it is holding a value from the last
 *    REST call and hoping. Do not use it.
 *  - **`LocalizedRunningState.occurrenceStartSeconds`** is the output of
 *    `localizeRunningState` — the SAME instant put through this function, i.e.
 *    seconds from the **BROWSER's** local midnight. That is the one to build
 *    planned timings from, and `localizeRunningStateForBrowser` below is how
 *    this app gets it.
 *
 * The distinction used to rest on this comment alone. It is now carried by the
 * types: `getRunningElementTimings` accepts only `LocalizedRunningState`, so a
 * wire `RunningState` can no longer reach the projection un-localized, and the
 * envelope's number is not on that type at all. The comment survives because the
 * two names still collide, and a reader who picks the wrong one gets no error —
 * just quietly wrong times for anyone outside the timeline's zone.
 *
 * @param iso - Absolute ISO-8601 instant
 * @param nowAbsoluteSeconds - "Now" in the absolute-seconds frame
 * @param nowMs - "Now" as a Unix epoch millisecond timestamp
 * @returns The instant in absolute seconds, or null if unparseable
 */
export function isoInstantToAbsoluteSeconds(
  iso: string | null | undefined,
  nowAbsoluteSeconds: number,
  nowMs: number
): number | null {
  if (!iso) return null;
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return null;
  return Math.round(nowAbsoluteSeconds + (ms - nowMs) / 1000);
}

/**
 * Put a wire `RunningState` into the browser's absolute-seconds frame.
 *
 * This is the app's ONE conversion point. Everything downstream — the hero
 * countdown, the schedule list, the projection, the banner — consumes the
 * `LocalizedRunningState` this returns and never touches an instant again. Call
 * it where the state is owned (App.tsx), not per consumer: two consumers each
 * localizing is two chances to anchor against different clocks, which is the
 * same shape of defect as two consumers each holding their own copy of the hook.
 *
 * ## Why the null handling is what it is
 *
 * `localizeRunningState` demands `(iso) => number`. It has no null channel and
 * should not: a half-localized run has no meaning, and inventing a filler second
 * count for an instant that would not parse is exactly the kind of silent wrong
 * number this whole migration exists to remove. `isoInstantToAbsoluteSeconds`
 * DOES have a null channel, for an unparseable instant. The two are reconciled
 * by refusing the WHOLE state: a malformed payload renders as no run at all —
 * the plain planned timeline — rather than as a run with one nonsense row in it.
 *
 * The server validates every instant with Zod, so this is a backstop, not a
 * path. It is here because the alternative failure mode is invisible.
 *
 * @param state - Running state as it arrives on the wire, or null when nothing is running
 * @param nowAbsoluteSeconds - "Now" in the browser's absolute-seconds frame
 * @param nowMs - The SAME "now" as a Unix epoch millisecond timestamp
 * @returns The run in the browser's frame, or null when there is none / it is unusable
 */
export function localizeRunningStateForBrowser(
  state: RunningState | null | undefined,
  nowAbsoluteSeconds: number,
  nowMs: number
): LocalizedRunningState | null {
  if (!state) return null;

  let unparseable = false;
  const toAbsoluteSeconds = (iso: string): number => {
    const seconds = isoInstantToAbsoluteSeconds(iso, nowAbsoluteSeconds, nowMs);
    if (seconds === null) {
      unparseable = true;
      return nowAbsoluteSeconds;
    }
    return seconds;
  };

  const localized = localizeRunningState(state, toAbsoluteSeconds);
  return unparseable ? null : localized;
}

/**
 * The absolute second a run's PLANNED timings must be built from.
 *
 * Reads like a bug — it un-does part of the localization that was just done — so
 * here is exactly why the subtraction is there.
 *
 * `timeOffset` shifts the OBSERVER'S CLOCK, not the schedule. A timeline with
 * `timeOffset` +15 min displays 10:15 when the server clock reads 10:00; the
 * element planned for 10:00 is still planned for 10:00. So an operator who marks
 * it at server-10:00 genuinely IS fifteen minutes late against the plan, and the
 * server reports variance +900. (This is the same rule the rest of the app
 * already follows: `getTimeWithOffset(timeOffset)` offsets "now" and nothing
 * else, and `occurrenceStartAbsolute` — the baseline used when no run exists —
 * carries no offset at all.)
 *
 * Two invariants have to hold at once, and they pull in opposite directions:
 *
 *  1. **Observations and `nowSeconds` must share one frame.** They are subtracted
 *     from each other constantly, so `timeOffset` must be in both or neither.
 *     `localizeRunningStateForBrowser` is anchored on the OFFSET clock, which
 *     puts every observation in the same frame as `currentForCalc`. ✓
 *  2. **The planned baseline must NOT be offset.** The schedule did not move.
 *
 * Because the localizer converts every instant with one anchor — including
 * `occurrenceStart` — `occurrenceStartSeconds` comes back carrying `timeOffset`
 * too. That is right for invariant 1 and wrong for invariant 2, so the offset is
 * taken back off here, at the one place the planned baseline is derived.
 *
 * Skip this correction and `timeOffset` cancels out of every variance: planned,
 * now, and actuals all shift by the same constant, the subtractions absorb it,
 * and running state silently ignores an offset the server is honouring. That is
 * the same SHAPE of defect as the cross-timezone one — client and server
 * disagreeing by a constant — with a different constant.
 *
 * The alternative, localizing twice (once offset for observations, once not for
 * the baseline), would mean two anchors and two chances to drift. One conversion
 * plus one explicit, named correction is the smaller surface.
 *
 * @param state - Localized running state (produced by `localizeRunningStateForBrowser`)
 * @param timeOffset - The timeline's display offset in seconds, as passed to `getTimeWithOffset`
 * @returns The run's occurrence start with the observer's clock offset removed
 */
export function runPlannedBaselineSeconds(
  state: LocalizedRunningState,
  timeOffset: number
): number {
  return state.occurrenceStartSeconds - timeOffset;
}

/**
 * Classify a variance. Positive is behind (running late), negative is ahead.
 */
export function varianceTone(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): VarianceTone {
  if (!Number.isFinite(seconds) || Math.abs(seconds) <= toleranceSeconds) return 'on-time';
  return seconds > 0 ? 'behind' : 'ahead';
}

/**
 * Format the magnitude of a variance as M:SS (or H:MM:SS past an hour), with an
 * explicit sign. Uses U+2212 MINUS SIGN so a negative reads unambiguously at a
 * glance rather than as a hyphen.
 */
export function formatVarianceOffset(seconds: number): string {
  const rounded = Math.round(Number.isFinite(seconds) ? seconds : 0);
  const sign = rounded > 0 ? '+' : rounded < 0 ? '−' : '';
  const abs = Math.abs(rounded);
  const hours = Math.floor(abs / 3600);
  const mins = Math.floor((abs % 3600) / 60);
  const secs = abs % 60;
  const body = hours > 0
    ? `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    : `${mins}:${secs.toString().padStart(2, '0')}`;
  return `${sign}${body}`;
}

/**
 * Full accessible variance readout — sign, magnitude and word.
 *
 * Colour is only ever an ENHANCEMENT of this string; the string alone must
 * carry the meaning for anyone who cannot perceive the colour.
 *
 * @example formatVariance(45)   // '+0:45 behind'
 * @example formatVariance(-30)  // '−0:30 ahead'
 * @example formatVariance(2)    // 'On time'
 */
export function formatVariance(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): string {
  const tone = varianceTone(seconds, toleranceSeconds);
  if (tone === 'on-time') return 'On time';
  return `${formatVarianceOffset(seconds)} ${tone}`;
}

/**
 * Variance magnitude with NO sign — `1:10`, `40s`, `0:00`.
 *
 * The sign is redundant next to the direction word and actively harmful in the ahead
 * case: `−16:50 ahead` reads as a double negative, and a reader who resolves it wrongly
 * gets the direction exactly backwards. Pair this with `varianceWord` (and, where space
 * allows, `varianceGlyph`) so direction is carried once, in words.
 *
 * @example formatVarianceMagnitude(70)   // '1:10'
 * @example formatVarianceMagnitude(-40)  // '40s'
 */
export function formatVarianceMagnitude(seconds: number): string {
  const abs = Math.abs(Math.round(Number.isFinite(seconds) ? seconds : 0));
  if (abs < 60) return `${abs}s`;
  const hours = Math.floor(abs / 3600);
  const mins = Math.floor((abs % 3600) / 60);
  const secs = abs % 60;
  return hours > 0
    ? `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    : `${mins}:${secs.toString().padStart(2, '0')}`;
}

/** The direction word on its own. The non-colour channel — never drop it in a reflow. */
export function varianceWord(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): string {
  const tone = varianceTone(seconds, toleranceSeconds);
  return tone === 'on-time' ? 'on time' : tone;
}

/**
 * A shape cue to sit alongside colour and word.
 *
 * Three independent channels — colour, word, glyph — means the readout survives
 * greyscale, a washed-out projector, and a colour-blind operator.
 */
export function varianceGlyph(tone: VarianceTone): string {
  if (tone === 'behind') return '\u25B2';
  if (tone === 'ahead') return '\u25BC';
  return '\u25CF';
}

/**
 * Unsigned, worded variance for display — `1:10 behind`, `40s ahead`, `On time`.
 *
 * Supersedes `formatVariance` on every live surface. `formatVariance` is retained for
 * callers that still want the signed form.
 */
export function formatVarianceWords(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): string {
  const tone = varianceTone(seconds, toleranceSeconds);
  if (tone === 'on-time') return 'On time';
  return `${formatVarianceMagnitude(seconds)} ${tone}`;
}

/**
 * Headline pace readout for the running banner.
 *
 * Deliberately worded differently from `formatVariance` in the AHEAD case.
 * `summarizeVariance().currentVariance` is measured at the current element's
 * hand-off — projected end minus planned end — and the projected end is
 * `max(actualStart + duration, now)`. So an element marked 11 minutes early
 * reports −680 the instant it is marked and sits there: it is a claim about
 * where the hand-off WILL land if the element runs its planned length, not a
 * measurement of time already saved. "11:20 ahead" would overclaim that.
 *
 * Behind is not hedged, because it IS elapsed: the projected end only exceeds
 * the planned end once the clock has actually passed it.
 *
 * @example formatPaceVariance(196)  // '+3:16 behind'
 * @example formatPaceVariance(-680) // '−11:20 ahead of the next hand-off'
 * @example formatPaceVariance(2)    // 'On time'
 */
export function formatPaceVariance(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): string {
  const tone = varianceTone(seconds, toleranceSeconds);
  if (tone === 'on-time') return 'On time';
  if (tone === 'behind') return `${formatVarianceOffset(seconds)} behind`;
  return `${formatVarianceOffset(seconds)} ahead of the next hand-off`;
}

/**
 * The sentence that keeps the pace readout honest, or null when none is needed.
 *
 * Only the ahead case needs one — see `formatPaceVariance`.
 */
export function paceExplanation(
  seconds: number,
  toleranceSeconds: number = ON_TIME_TOLERANCE_SECONDS
): string | null {
  if (varianceTone(seconds, toleranceSeconds) !== 'ahead') return null;
  return 'Ahead assumes the element in progress runs its planned length.';
}

/** How a finished run actually went. */
export interface RunOutcome {
  /** Where the last element that RAN ended, against where it was planned to end. */
  finalVariance: number;
  /** The last element that actually ran, or null if the run ended with nothing marked. */
  lastRan: RunningElement | null;
  /** Elements the run reached — assumed or reported. */
  ran: number;
  /**
   * Of those, how many the operator actually reported.
   *
   * The honest half of the summary. Under "assume the plan is being followed" most
   * elements are unreported by design, so a run nobody touched would otherwise review
   * as one that ran perfectly. This is what keeps measured and assumed separable where
   * it matters most — in the record of how it went.
   */
  reported: number;
  /** Elements the run ended before reaching. */
  notReached: number;
}

/**
 * Roll up a COMPLETED run from its per-element data.
 *
 * `summarizeVariance` cannot do this: every one of its figures is derived from the
 * element with status `'current'`, and a completed run has none by construction —
 * so the server's summary legitimately reports all zeros once the run ends. Left at
 * that, a run that finished eleven minutes ahead reports nothing, anywhere, which
 * is the opposite of §6.3's "remains readable so a viewer can still see how the run
 * finished".
 *
 * The headline is measured at the last element that ACTUALLY RAN. Elements the run
 * ended before reaching are excluded deliberately — chaining forward through things
 * that never happened would forecast a finish for a run that is already over.
 *
 * @param running - Output of getRunningElementTimings for a completed run
 */
export function summarizeCompletedRun(running: RunningElement[]): RunOutcome {
  // Reached, not reported: an element the run passed is part of how the occurrence went
  // whether or not anyone said anything about it. `reported` keeps the distinction.
  const reachedElements = running.filter((el) => el.status !== 'upcoming');
  const lastRan = reachedElements.length > 0 ? reachedElements[reachedElements.length - 1] : null;

  return {
    finalVariance: lastRan ? lastRan.endTime - lastRan.plannedEnd : 0,
    lastRan,
    ran: reachedElements.length,
    reported: running.filter((el) => el.actualStart !== null).length,
    notReached: running.filter((el) => el.status === 'upcoming').length,
  };
}

/** Tailwind text colour for a tone. Redundant with the label, never a substitute for it. */
export function varianceColorClass(tone: VarianceTone): string {
  switch (tone) {
    case 'behind': return 'text-red-600 dark:text-red-400';
    case 'ahead': return 'text-blue-600 dark:text-blue-400';
    default: return 'text-green-600 dark:text-green-400';
  }
}
